import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    CircularProgress,
    Alert,
    Tooltip,
    Card,
    CardContent,
    TableSortLabel,
    Switch,
    FormControlLabel,
    Modal, // For the assignment modal
    Select, // For admin selection
    MenuItem, // For admin selection
    FormControl, // For admin selection
    InputLabel, // For admin selection
    IconButton 
} from "@mui/material";
import { Delete, Edit, Save, Cancel, Download, SupervisorAccount, Close as CloseIcon } from "@mui/icons-material"; // Added SupervisorAccount & CloseIcon
import config from './config.js';
import api from './api';

// Modal style
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};


function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function descendingComparator(a, b, orderBy) {
  let valA = a[orderBy];
  let valB = b[orderBy];
  const aIsNull = valA === null || typeof valA === 'undefined';
  const bIsNull = valB === null || typeof valB === 'undefined';

  if (aIsNull && bIsNull) return 0;
  if (aIsNull) return 1; 
  if (bIsNull) return -1;

  if (['cohort_id', 'spi_is_visible', 'prs_is_visible', 'charisma_is_visible', 'brand_is_visible'].includes(orderBy)) { 
    valA = Number(valA) || 0;
    valB = Number(valB) || 0;
  } else if (orderBy === 'cohort_name') {
    valA = String(valA).toLowerCase();
    valB = String(valB).toLowerCase();
  }

  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
}


function CohortManagement() {
    const [cohorts, setCohorts] = useState([]);
    const [newCohortName, setNewCohortName] = useState("");
    const [newCohortSpiVisible, setNewCohortSpiVisible] = useState(false); 
    const [newCohortPrsVisible, setNewCohortPrsVisible] = useState(false);
    const [newCohortCharismaVisible, setNewCohortCharismaVisible] = useState(false);
    const [newCohortBrandVisible, setNewCohortBrandVisible] = useState(false); // NEW
    const [editingCohort, setEditingCohort] = useState(null); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [filterText, setFilterText] = useState("");
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('cohort_name');

    const [downloadingSpiCohortId, setDownloadingSpiCohortId] = useState(null);
    const [downloadingCharismaCohortId, setDownloadingCharismaCohortId] = useState(null); 
    const [downloadError, setDownloadError] = useState(null);

    // State for Batch Admin Assignment Modal
    const [isAssignAdminModalOpen, setIsAssignAdminModalOpen] = useState(false);
    const [selectedCohortForAdminAssignment, setSelectedCohortForAdminAssignment] = useState(null);
    const [assignableAdmins, setAssignableAdmins] = useState([]);
    const [selectedAdminForCohort, setSelectedAdminForCohort] = useState('');
    const [assignmentLoading, setAssignmentLoading] = useState(false);
    const [assignmentError, setAssignmentError] = useState(null);
    const [assignmentSuccess, setAssignmentSuccess] = useState(null);


    const fetchCohorts = useCallback(async () => {
        setLoading(true); setError(null); setSuccessMessage(null); setDownloadError(null);
        try {
            const response = await api.get(`${config.backendUrl}/cohorts`);
            const data = response.data;
            if (Array.isArray(data)) {
                setCohorts(data.map(c => ({ 
                    ...c, 
                    spi_is_visible: !!c.spi_is_visible,
                    prs_is_visible: !!c.prs_is_visible,
                    charisma_is_visible: !!c.charisma_is_visible,
                    brand_is_visible: !!c.brand_is_visible // NEW
                }))); 
            } else {
                throw new Error("Received invalid data format for cohorts.");
            }
        } catch (error) {
            setError(error.response?.data?.message || error.message || "Failed to fetch cohorts.");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchAssignableAdmins = useCallback(async () => {
        try {
            const response = await api.get(`${config.backendUrl}/users`); // Assuming this fetches all users
            const adminsAndMods = response.data.filter(user => user.role_id === 1 || user.role_id === 2);
            setAssignableAdmins(adminsAndMods);
        } catch (err) {
            console.error("Failed to fetch assignable admins/mods:", err);
            setAssignmentError("Failed to load list of admins/moderators.");
        }
    }, []);


    useEffect(() => {
        fetchCohorts();
    }, [fetchCohorts]);

    useEffect(() => { // Fetch admins when modal is about to open or is open
        if (isAssignAdminModalOpen && assignableAdmins.length === 0) {
            fetchAssignableAdmins();
        }
    }, [isAssignAdminModalOpen, assignableAdmins.length, fetchAssignableAdmins]);


    const handleOpenAssignAdminModal = (cohort) => {
        setSelectedCohortForAdminAssignment(cohort);
        setSelectedAdminForCohort(''); // Reset selection
        setAssignmentError(null);
        setAssignmentSuccess(null);
        setIsAssignAdminModalOpen(true);
    };

    const handleCloseAssignAdminModal = () => {
        setIsAssignAdminModalOpen(false);
        setSelectedCohortForAdminAssignment(null);
        setSelectedAdminForCohort('');
    };

    const handleAssignAdminToCohortTeams = async () => {
        if (!selectedCohortForAdminAssignment || !selectedAdminForCohort) {
            setAssignmentError("Please select a cohort and an admin/moderator.");
            return;
        }
        setAssignmentLoading(true);
        setAssignmentError(null);
        setAssignmentSuccess(null);
        try {
            const response = await api.post(
                `${config.backendUrl}/api/admins/${selectedAdminForCohort}/cohort_assignments`,
                { cohort_id: selectedCohortForAdminAssignment.cohort_id }
            );
            setAssignmentSuccess(response.data.message || "Admin/Moderator processed for cohort teams successfully.");
            // Optionally, refresh data or close modal after a delay
            setTimeout(() => {
                handleCloseAssignAdminModal();
            }, 3000); 
        } catch (err) {
            setAssignmentError(err.response?.data?.message || err.message || "Failed to assign admin to cohort teams.");
        } finally {
            setAssignmentLoading(false);
        }
    };


    const handleFilterChange = (event) => { setFilterText(event.target.value); };
    const handleRequestSort = (property) => { const isAsc = orderBy === property && order === 'asc'; setOrder(isAsc ? 'desc' : 'asc'); setOrderBy(property); };
    const handleInputChange = (event) => { setNewCohortName(event.target.value); };
    const handleNewSpiVisibleChange = (event) => { setNewCohortSpiVisible(event.target.checked); }; 
    const handleNewPrsVisibleChange = (event) => { setNewCohortPrsVisible(event.target.checked); };
    const handleNewCharismaVisibleChange = (event) => { setNewCohortCharismaVisible(event.target.checked); };
    const handleNewBrandVisibleChange = (event) => { setNewCohortBrandVisible(event.target.checked); }; // NEW

    const handleCreateCohort = async () => {
        if (!newCohortName.trim()) { setError("Cohort name cannot be empty."); return; }
        setError(null); setSuccessMessage(null); setDownloadError(null);
        try {
            const response = await api.post(`${config.backendUrl}/cohorts`, { 
                cohort_name: newCohortName,
                spi_is_visible: newCohortSpiVisible,
                prs_is_visible: newCohortPrsVisible,
                charisma_is_visible: newCohortCharismaVisible,
                brand_is_visible: newCohortBrandVisible // NEW
            });
            const newCohortData = response.data;
            setCohorts(prevCohorts => [...prevCohorts, { 
                ...newCohortData, 
                spi_is_visible: !!newCohortData.spi_is_visible,
                prs_is_visible: !!newCohortData.prs_is_visible,
                charisma_is_visible: !!newCohortData.charisma_is_visible,
                brand_is_visible: !!newCohortData.brand_is_visible // NEW
            }]);
            setNewCohortName("");
            setNewCohortSpiVisible(false); 
            setNewCohortPrsVisible(false);
            setNewCohortCharismaVisible(false);
            setNewCohortBrandVisible(false); // NEW
            setSuccessMessage(`Cohort "${newCohortData.cohort_name}" created successfully.`);
        } catch (error) {
             setError(error.response?.data?.message || error.message || "Failed to create cohort.");
        }
    };

    const handleEdit = (cohort) => {
        setEditingCohort({ 
            cohort_id: cohort.cohort_id, 
            cohort_name: cohort.cohort_name, 
            spi_is_visible: !!cohort.spi_is_visible,
            prs_is_visible: !!cohort.prs_is_visible,
            charisma_is_visible: !!cohort.charisma_is_visible,
            brand_is_visible: !!cohort.brand_is_visible // NEW
        });
        setError(null); setSuccessMessage(null); setDownloadError(null);
    };

    const handleCancelEdit = () => { setEditingCohort(null); };

    const handleEditingInputChange = (event) => {
        if (editingCohort) {
            const { name, value, type, checked } = event.target;
            setEditingCohort(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value 
            }));
        }
    };
    
    const handleEditSpiVisibilityChange = (event) => {
        if (editingCohort) {
            setEditingCohort(prev => ({ ...prev, spi_is_visible: event.target.checked }));
        }
    };
    
    const handleEditPrsVisibilityChange = (event) => { 
        if (editingCohort) {
            setEditingCohort(prev => ({ ...prev, prs_is_visible: event.target.checked }));
        }
    };

    const handleEditCharismaVisibilityChange = (event) => { 
        if (editingCohort) {
            setEditingCohort(prev => ({ ...prev, charisma_is_visible: event.target.checked }));
        }
    };

    const handleEditBrandVisibilityChange = (event) => { // NEW
        if (editingCohort) {
            setEditingCohort(prev => ({...prev, brand_is_visible: event.target.checked }));
        }
    };

    const handleUpdateCohort = async () => {
         if (!editingCohort || !editingCohort.cohort_name || !editingCohort.cohort_name.trim()) {
            setError("Cohort name cannot be empty during edit.");
            return;
        }
        setError(null); setSuccessMessage(null); setDownloadError(null);
        try {
             const response = await api.put(`${config.backendUrl}/cohorts/${editingCohort.cohort_id}`, { 
                cohort_name: editingCohort.cohort_name,
                spi_is_visible: editingCohort.spi_is_visible,
                prs_is_visible: editingCohort.prs_is_visible,
                charisma_is_visible: editingCohort.charisma_is_visible,
                brand_is_visible: editingCohort.brand_is_visible // NEW
             });
             const updatedCohortData = response.data;
             setCohorts(prevCohorts =>
                prevCohorts.map(c =>
                    c.cohort_id === updatedCohortData.cohort_id ? { 
                        ...updatedCohortData, 
                        spi_is_visible: !!updatedCohortData.spi_is_visible,
                        prs_is_visible: !!updatedCohortData.prs_is_visible,
                        charisma_is_visible: !!updatedCohortData.charisma_is_visible,
                        brand_is_visible: !!updatedCohortData.brand_is_visible // NEW
                    } : c
                )
            );
            handleCancelEdit();
            setSuccessMessage(`Cohort "${updatedCohortData.cohort_name}" updated successfully.`);
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || "Failed to update cohort.";
            setError(errorMsg);
        }
    };

    const handleDeleteCohort = async (cohortId, cohortName) => {
        if (!window.confirm(`Are you sure you want to delete the cohort "${cohortName}"? This will also remove its assessment settings and any associated reports.`)) { return; }
        setError(null); setSuccessMessage(null); setDownloadError(null);
        try {
            await api.delete(`${config.backendUrl}/cohorts/${cohortId}`);
            setCohorts(prevCohorts => prevCohorts.filter(c => c.cohort_id !== cohortId));
            setSuccessMessage(`Cohort "${cohortName}" deleted successfully.`);
            if (editingCohort?.cohort_id === cohortId) { handleCancelEdit(); }
        } catch (error) {
            setError(error.response?.data?.message || error.message || "Failed to delete cohort.");
        }
    };

    const handleDownloadSpiCsv = async (cohortId, cohortName) => {
        setDownloadingSpiCohortId(cohortId);
        setDownloadError(null); setError(null); setSuccessMessage(null); 
        try {
            const response = await api.get(`${config.backendUrl}/api/admin/spi/responses/download?cohort_id=${cohortId}`, {
                responseType: 'blob',
            });
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none'; a.href = url;
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            a.download = `spi_responses_cohort_${cohortName.replace(/\s+/g, '_')}_${cohortId}_${timestamp}.csv`;
            document.body.appendChild(a); a.click();
            window.URL.revokeObjectURL(url); a.remove();
            setSuccessMessage(`SPI Report for cohort "${cohortName}" download initiated.`);
        } catch (err) {
            let message = "Failed to download SPI report.";
            if (err.response && err.response.data) {
                if (err.response.data instanceof Blob && err.response.data.type === "application/json") {
                    try {
                        const errorJsonText = await err.response.data.text();
                        const parsedError = JSON.parse(errorJsonText);
                        message = parsedError.message || message;
                    } catch (parseErr) { /* Fallback */ }
                } else if (typeof err.response.data === 'string') {
                     message = err.response.data;
                } else if (err.response.data.message) {
                     message = err.response.data.message;
                }
            } else if (err.message) { message = err.message; }
            setDownloadError(message);
        } finally {
            setDownloadingSpiCohortId(null);
        }
    };

    const handleDownloadCharismaCsv = async (cohortId, cohortName) => {
        setDownloadingCharismaCohortId(cohortId);
        setDownloadError(null); setError(null); setSuccessMessage(null);
        try {
            const response = await api.get(`${config.backendUrl}/api/admin/charisma/responses/download?cohort_id=${cohortId}`, {
                responseType: 'blob',
            });
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none'; a.href = url;
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            a.download = `charisma_diagnostic_cohort_${cohortName.replace(/\s+/g, '_')}_${cohortId}_${timestamp}.csv`;
            document.body.appendChild(a); a.click();
            window.URL.revokeObjectURL(url); a.remove();
            setSuccessMessage(`Charisma Diagnostic Report for cohort "${cohortName}" download initiated.`);
        } catch (err) {
            let message = "Failed to download Charisma Diagnostic report.";
             if (err.response && err.response.data) {
                if (err.response.data instanceof Blob && err.response.data.type === "application/json") {
                    try {
                        const errorJsonText = await err.response.data.text();
                        const parsedError = JSON.parse(errorJsonText);
                        message = parsedError.message || message;
                    } catch (parseErr) { /* Fallback */ }
                } else if (typeof err.response.data === 'string') {
                     message = err.response.data;
                } else if (err.response.data.message) {
                     message = err.response.data.message;
                }
            } else if (err.message) { message = err.message; }
            setDownloadError(message);
        } finally {
            setDownloadingCharismaCohortId(null);
        }
    };


    const visibleCohorts = useMemo(() => {
        let filtered = cohorts;
        if (filterText) {
            const lowerCaseFilter = filterText.toLowerCase();
            filtered = cohorts.filter(cohort =>
                (cohort.cohort_name ?? '').toLowerCase().includes(lowerCaseFilter) ||
                (cohort.cohort_id ?? '').toString().includes(lowerCaseFilter)
            );
        }
        return stableSort(filtered, getComparator(order, orderBy));
    }, [cohorts, filterText, order, orderBy]);


    return (
        <Box sx={{ p: 2, maxWidth: '1250px', margin: 'auto' }}> 
            <Typography variant="h4" gutterBottom component="h1"> Cohort Management </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {successMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}
            {downloadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError(null)}>{downloadError}</Alert>}

            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                     <Typography variant="h6" gutterBottom>Create New Cohort</Typography>
                     <Box component="form" onSubmit={(e) => { e.preventDefault(); handleCreateCohort(); }} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField 
                            label="New Cohort Name" 
                            value={newCohortName} 
                            onChange={handleInputChange} 
                            variant="outlined" 
                            size="small" 
                            required 
                        />
                        <FormControlLabel
                            control={<Switch checked={newCohortSpiVisible} onChange={handleNewSpiVisibleChange} />}
                            label="Self-Perception Inventory (SPI) Visible"
                        />
                        <FormControlLabel
                            control={<Switch checked={newCohortPrsVisible} onChange={handleNewPrsVisibleChange} />}
                            label="Professional Reputation Survey (PRS) Visible"
                        />
                        <FormControlLabel
                            control={<Switch checked={newCohortCharismaVisible} onChange={handleNewCharismaVisibleChange} />}
                            label="Charisma Diagnostic Visible"
                        />
                        <FormControlLabel
                            control={<Switch checked={newCohortBrandVisible} onChange={handleNewBrandVisibleChange} />}
                            label="Brand Clarity Visible"
                        />
                        <Button type="submit" variant="contained" color="primary" sx={{ alignSelf: 'flex-start' }}> Create Cohort </Button>
                     </Box>
                </CardContent>
            </Card>

            <TextField label="Filter Cohorts (by Name or ID)" variant="outlined" fullWidth size="small" value={filterText} onChange={handleFilterChange} sx={{ mb: 2 }} />

            <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>Existing Cohorts</Typography>
            {loading ? ( <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}> <CircularProgress /> </Box> ) : (
                <TableContainer component={Paper} sx={{mb: 2}}>
                    <Table stickyHeader aria-label="cohort table">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{width: '8%'}}> <TableSortLabel active={orderBy === 'cohort_id'} direction={orderBy === 'cohort_id' ? order : 'asc'} onClick={() => handleRequestSort('cohort_id')} > ID </TableSortLabel> </TableCell>
                                <TableCell sx={{width: '20%'}}> <TableSortLabel active={orderBy === 'cohort_name'} direction={orderBy === 'cohort_name' ? order : 'asc'} onClick={() => handleRequestSort('cohort_name')} > Cohort Name </TableSortLabel> </TableCell>
                                <TableCell sx={{width: '10%'}} align="center">
                                    <TableSortLabel active={orderBy === 'spi_is_visible'} direction={orderBy === 'spi_is_visible' ? order : 'asc'} onClick={() => handleRequestSort('spi_is_visible')}>
                                        SPI Vis.
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{width: '10%'}} align="center">
                                    <TableSortLabel active={orderBy === 'prs_is_visible'} direction={orderBy === 'prs_is_visible' ? order : 'asc'} onClick={() => handleRequestSort('prs_is_visible')}>
                                        PRS Vis.
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{width: '10%'}} align="center">
                                    <TableSortLabel active={orderBy === 'charisma_is_visible'} direction={orderBy === 'charisma_is_visible' ? order : 'asc'} onClick={() => handleRequestSort('charisma_is_visible')}>
                                        Charisma Vis.
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{width: '10%'}} align="center">
                                    <TableSortLabel active={orderBy === 'brand_is_visible'} direction={orderBy === 'brand_is_visible' ? order : 'asc'} onClick={() => handleRequestSort('brand_is_visible')}>
                                        Brand Vis.
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center" sx={{width: '32%'}}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleCohorts && visibleCohorts.length > 0 ? (
                                visibleCohorts.map((cohort) => (
                                <TableRow key={cohort.cohort_id} hover sx={ editingCohort?.cohort_id === cohort.cohort_id ? { backgroundColor: 'action.hover' } : {} }>
                                    <TableCell>{cohort.cohort_id}</TableCell>
                                    <TableCell>
                                        {editingCohort?.cohort_id === cohort.cohort_id ? (
                                            <TextField
                                                value={editingCohort.cohort_name}
                                                name="cohort_name" 
                                                onChange={handleEditingInputChange}
                                                size="small" variant="outlined" fullWidth required autoFocus
                                            />
                                        ) : ( cohort.cohort_name )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {editingCohort?.cohort_id === cohort.cohort_id ? (
                                            <Switch
                                                checked={editingCohort.spi_is_visible}
                                                onChange={handleEditSpiVisibilityChange}
                                                name="spi_is_visible"
                                            />
                                        ) : (
                                            <Switch checked={!!cohort.spi_is_visible} disabled />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {editingCohort?.cohort_id === cohort.cohort_id ? (
                                            <Switch
                                                checked={editingCohort.prs_is_visible}
                                                onChange={handleEditPrsVisibilityChange} 
                                                name="prs_is_visible"
                                            />
                                        ) : (
                                            <Switch checked={!!cohort.prs_is_visible} disabled />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {editingCohort?.cohort_id === cohort.cohort_id ? (
                                            <Switch
                                                checked={editingCohort.charisma_is_visible}
                                                onChange={handleEditCharismaVisibilityChange} 
                                                name="charisma_is_visible"
                                            />
                                        ) : (
                                            <Switch checked={!!cohort.charisma_is_visible} disabled />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {editingCohort?.cohort_id === cohort.cohort_id ? (
                                            <Switch
                                                checked={editingCohort.brand_is_visible}
                                                onChange={handleEditBrandVisibilityChange} 
                                                name="brand_is_visible"
                                            />
                                        ) : (
                                            <Switch checked={!!cohort.brand_is_visible} disabled />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                         <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            {editingCohort?.cohort_id === cohort.cohort_id ? (
                                                <>
                                                    <Tooltip title="Save Changes"><Button variant="contained" size="small" color="primary" onClick={handleUpdateCohort} startIcon={<Save />} sx={{ minWidth: 'auto', px: 1 }} disabled={!editingCohort.cohort_name?.trim()} /></Tooltip>
                                                    <Tooltip title="Cancel Edit"><Button variant="outlined" size="small" color="secondary" onClick={handleCancelEdit} startIcon={<Cancel />} sx={{ minWidth: 'auto', px: 1 }} /></Tooltip>
                                                </>
                                            ) : (
                                                <>
                                                    <Tooltip title="Edit Cohort"><Button variant="outlined" size="small" color="primary" startIcon={<Edit />} onClick={() => handleEdit(cohort)} sx={{ minWidth: 'auto', px: 1 }} /></Tooltip>
                                                    <Tooltip title="Delete Cohort"><Button variant="outlined" size="small" color="error" startIcon={<Delete />} onClick={() => handleDeleteCohort(cohort.cohort_id, cohort.cohort_name)} sx={{ minWidth: 'auto', px: 1 }} /></Tooltip>
                                                </>
                                            )}
                                            <Tooltip title="Assign Admin to All Teams in this Cohort">
                                                <Button variant="outlined" size="small" color="warning" startIcon={<SupervisorAccount />} onClick={() => handleOpenAssignAdminModal(cohort)} sx={{ minWidth: 'auto', px: 1, ml: 0.5 }}>
                                                    Assign Admin
                                                </Button>
                                            </Tooltip>
                                            <Tooltip title="Download SPI Report">
                                                <Button variant="outlined" size="small" color="info" startIcon={downloadingSpiCohortId === cohort.cohort_id ? <CircularProgress size={16} color="inherit"/> : <Download />} onClick={() => handleDownloadSpiCsv(cohort.cohort_id, cohort.cohort_name)} disabled={downloadingSpiCohortId === cohort.cohort_id || !cohort.spi_is_visible} sx={{ minWidth: 'auto', px: 1, ml: 0.5 }}>SPI CSV</Button>
                                            </Tooltip>
                                            <Tooltip title="Download Charisma Report">
                                                <Button variant="outlined" size="small" color="secondary" startIcon={downloadingCharismaCohortId === cohort.cohort_id ? <CircularProgress size={16} color="inherit"/> : <Download />} onClick={() => handleDownloadCharismaCsv(cohort.cohort_id, cohort.cohort_name)} disabled={downloadingCharismaCohortId === cohort.cohort_id || !cohort.charisma_is_visible} sx={{ minWidth: 'auto', px: 1, ml: 0.5 }}>Charisma CSV</Button>
                                            </Tooltip>
                                         </Box>
                                    </TableCell>
                                </TableRow>
                            ))) : (
                                 <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        {filterText ? "No cohorts match your filter." : "No cohorts found."}
                                    </TableCell>
                                 </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Modal for Assigning Admin to Cohort Teams */}
            <Modal
                open={isAssignAdminModalOpen}
                onClose={handleCloseAssignAdminModal}
                aria-labelledby="assign-admin-modal-title"
                aria-describedby="assign-admin-modal-description"
            >
                <Box sx={modalStyle}>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseAssignAdminModal}
                        sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography id="assign-admin-modal-title" variant="h6" component="h2">
                        Assign Admin/Moderator to All Teams in Cohort: <br/>
                        <Typography variant="subtitle1" component="span" color="primary">
                            {selectedCohortForAdminAssignment?.cohort_name}
                        </Typography>
                    </Typography>
                    
                    {assignmentError && <Alert severity="error" sx={{ mt: 2, mb:1 }}>{assignmentError}</Alert>}
                    {assignmentSuccess && <Alert severity="success" sx={{ mt: 2, mb:1 }}>{assignmentSuccess}</Alert>}

                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel id="select-admin-label">Select Admin/Moderator</InputLabel>
                        <Select
                            labelId="select-admin-label"
                            id="select-admin"
                            value={selectedAdminForCohort}
                            label="Select Admin/Moderator"
                            onChange={(e) => setSelectedAdminForCohort(e.target.value)}
                            disabled={assignmentLoading}
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {assignableAdmins.map((admin) => (
                                <MenuItem key={admin.user_id} value={admin.user_id}>
                                    {admin.name} ({admin.email}) - Role: {admin.role_id === 1 ? 'Admin' : 'Moderator'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button onClick={handleCloseAssignAdminModal} variant="outlined" disabled={assignmentLoading}>Cancel</Button>
                        <Button 
                            onClick={handleAssignAdminToCohortTeams} 
                            variant="contained" 
                            color="primary"
                            disabled={assignmentLoading || !selectedAdminForCohort}
                            startIcon={assignmentLoading ? <CircularProgress size={20} color="inherit"/> : null}
                        >
                            Assign to All Teams
                        </Button>
                    </Box>
                </Box>
            </Modal>

        </Box>
    );
}

export default CohortManagement;
